import { useNavigate } from "@tanstack/react-router";
import type { SpaceSummary } from "@nebulize/sdk";

import {
  Button,
  CopyField,
  EmptyState,
  Grid,
  MetricTile,
  Page,
  Panel,
  Stack,
  StatusBadge,
} from "@/components/ui";
import { accountAddress, broadcastAddress } from "@/lib/env";

import { Paged } from "../components/Paged";
import { SpaceVerbs } from "../components/SpaceVerbs";
import { Dot, Facts, Muted } from "../components/Text";
import { useSpaceActivities } from "../spaces/lib/queries";

import { QueryPanel } from "./components/QueryPanel";
import {
  useAccount,
  useCreateSpace,
  useEntitlement,
  useFeeds,
  useLoadedSpace,
  useMachines,
  useSpacePage,
} from "./lib/queries";
import { BILLED_QUOTAS, quotaLabel } from "./lib/present";

function matchSpace(row: SpaceSummary, needle: string): boolean {
  return `${row.space.name ?? ""} ${row.space.join_code}`.toLowerCase().includes(needle);
}

/** The account root: what is loaded, what is running, what the plan allows, and a way in. */
export function ConsoleHome() {
  const navigate = useNavigate();
  const machines = useMachines();
  const feeds = useFeeds();
  const entitlement = useEntitlement();
  const spaces = useSpacePage();
  const loaded = useLoadedSpace();
  const createSpace = useCreateSpace();

  const page = spaces.data?.spaces ?? [];
  const loadedId = loaded.data?.space_id ?? null;
  const loadedRow = page.find((one) => one.space.space_id === loadedId) ?? null;
  const account = useAccount();
  const loadedActivities = useSpaceActivities(loadedId ?? "");
  const inLoaded = loadedActivities.rows;
  const handle = account.data?.handle ?? "";
  const runningSpaces = page.filter((one) => (one.live_activity_count ?? 0) > 0).length;
  const quotas = entitlement.data?.quotas ?? {};

  return (
    <Page
      title="Home"
      actions={
          <Button
            variant="primary"
            size="sm"
            loading={createSpace.isPending}
            onClick={() => {
              createSpace.mutate();
            }}
          >
          Create a space
        </Button>
      }
    >
      <Grid columns={4} gap={4}>
        <MetricTile label="Spaces" value={spaces.isPending ? "" : String(page.length)} />
        <MetricTile label="Running spaces" value={spaces.isPending ? "" : String(runningSpaces)} />
        <MetricTile
          label="Machines"
          value={machines.isPending ? "" : String(machines.data?.length ?? 0)}
        />
        <MetricTile label="Feeds" value={feeds.isPending ? "" : String(feeds.data?.length ?? 0)} />
      </Grid>

      <Panel
        title="Addresses"
        subtitle="These name the account, so loading another space keeps every one of them working."
      >
        <Stack gap={3}>
          {handle === "" ? (
            <EmptyState
              bare
              headline="No handle yet, so this account has no front door"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void navigate({ to: "/console/account" });
                  }}
                >
                  Claim one
                </Button>
              }
            />
          ) : (
            <CopyField label="Hand out" value={accountAddress(handle)} />
          )}
          <CopyField label="Paste into OBS" value={broadcastAddress()} />
        </Stack>
      </Panel>

      <QueryPanel
        title="Loaded space"
        rows={2}
        query={loaded}
        action={
          loadedId === null ? null : (
            <Stack direction="row" gap={3} align="center">
              <SpaceVerbs spaceId={loadedId} added={inLoaded} />
              {loadedRow === null ? null : (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void navigate({
                      to: "/console/spaces/$joinCode/control",
                      params: { joinCode: loadedRow.space.join_code },
                    });
                  }}
                >
                  Open control
                </Button>
              )}
            </Stack>
          )
        }
      >
        {(row) =>
          row === null ? (
            <EmptyState
              bare
              headline="None"
              action={
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    void navigate({ to: "/console/spaces" });
                  }}
                >
                  Spaces
                </Button>
              }
            />
          ) : (
            <div className="cx-row">
              <div className="cx-row-id">
                <Dot tone="live" />
                <b>{loadedRow?.space.name ?? row.space_id}</b>
                {loadedRow === null ? null : (
                  <span className="cx-code">{loadedRow.space.join_code}</span>
                )}
              </div>
              <Muted small>{`${String(loadedRow?.live_activity_count ?? 0)} running`}</Muted>
            </div>
          )
        }
      </QueryPanel>

      <QueryPanel
        title="Quotas"
        rows={2}
        query={entitlement}
        action={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              void navigate({ to: "/console/plan" });
            }}
          >
            Plan
          </Button>
        }
      >
        {() => (
          <Facts
            items={BILLED_QUOTAS.map((name) => ({
              label: quotaLabel(name),
              value: quotas[name] === undefined ? "not set" : String(quotas[name]),
            }))}
          />
        )}
      </QueryPanel>

      <QueryPanel title="Spaces" rows={4} query={spaces}>
        {(body) => (
          <Paged
            rows={body.spaces ?? []}
            pageSize={10}
            placeholder="Find a space"
            match={matchSpace}
          >
            {(rows) => (
              <Stack gap={3}>
                {rows.map((row) => (
                  <div className="cx-row" key={row.space.space_id}>
                    <div className="cx-row-id">
                      <Dot tone={(row.live_activity_count ?? 0) > 0 ? "live" : "off"} />
                      <b>{row.space.name ?? row.space.join_code}</b>
                      <span className="cx-code">{row.space.join_code}</span>
                    </div>
                    <Stack direction="row" gap={3} align="center">
                      {(row.live_activity_count ?? 0) > 0 ? (
                        <StatusBadge tone="success">
                          {`${String(row.live_activity_count)} running`}
                        </StatusBadge>
                      ) : (
                        <StatusBadge tone="neutral">stopped</StatusBadge>
                      )}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          void navigate({
                            to: "/console/spaces/$joinCode/control",
                            params: { joinCode: row.space.join_code },
                          });
                        }}
                      >
                        Open control
                      </Button>
                    </Stack>
                  </div>
                ))}
              </Stack>
            )}
          </Paged>
        )}
      </QueryPanel>
    </Page>
  );
}
